import { cn } from '../../lib/cn.js';

export type Choice<V extends string> = { value: V; label: string; hint?: string };

type SingleProps<V extends string> = {
  name: string;
  legend: string;
  choices: ReadonlyArray<Choice<V>>;
  value: V | undefined;
  onChange: (v: V) => void;
  error?: string;
};

export function SingleChoice<V extends string>({
  name,
  legend,
  choices,
  value,
  onChange,
  error,
}: SingleProps<V>) {
  return (
    <fieldset className="mb-6">
      <legend className="block font-heading font-bold text-base mb-3">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {choices.map((c) => {
          const selected = value === c.value;
          return (
            <label
              key={c.value}
              className={cn(
                'flex flex-col gap-1 p-3 rounded-lg border cursor-pointer',
                selected
                  ? 'border-gcm-brown bg-surface-raised'
                  : 'border-border-default bg-surface-raised hover:border-gcm-brown',
              )}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name={name}
                  value={c.value}
                  checked={selected}
                  onChange={() => onChange(c.value)}
                  className="accent-gcm-red"
                />
                <span className="font-heading font-semibold text-text-primary">{c.label}</span>
              </span>
              {c.hint && <span className="text-sm text-text-secondary pl-6">{c.hint}</span>}
            </label>
          );
        })}
      </div>
      {error && <p className="text-sm text-danger mt-2">{error}</p>}
    </fieldset>
  );
}

type MultiProps<V extends string> = {
  legend: string;
  choices: ReadonlyArray<Choice<V>>;
  value: ReadonlyArray<V>;
  onChange: (v: V[]) => void;
  error?: string;
};

export function MultiChoice<V extends string>({
  legend,
  choices,
  value,
  onChange,
  error,
}: MultiProps<V>) {
  function toggle(v: V) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }
  return (
    <fieldset className="mb-6">
      <legend className="block font-heading font-bold text-base mb-3">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {choices.map((c) => {
          const selected = value.includes(c.value);
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => toggle(c.value)}
              className={cn('gcm-chip', selected ? 'gcm-chip-selected' : 'gcm-chip-default')}
              aria-pressed={selected}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {error && <p className="text-sm text-danger mt-2">{error}</p>}
    </fieldset>
  );
}
